import { initializeRemoteConfig } from '@diamond/shared/utils';
import {
  getRemoteConfig,
  getValue,
  RemoteConfig,
} from 'firebase/remote-config';
import { useEffect, useState } from 'react';

// Global state for Remote Config initialization
let isRemoteConfigInitialized = false;
let remoteConfigInitPromise: Promise<RemoteConfig | undefined> | null = null;

const initializeRemoteConfigOnce = async () => {
  if (!isRemoteConfigInitialized && !remoteConfigInitPromise) {
    remoteConfigInitPromise = initializeRemoteConfig();
    await remoteConfigInitPromise;
    isRemoteConfigInitialized = true;
  } else if (remoteConfigInitPromise) {
    await remoteConfigInitPromise;
  }
};

const parseFeatureValue = (
  remoteValue: any,
  defaultValue: boolean
): boolean => {
  if (remoteValue.getSource() === 'static') return defaultValue;

  const stringValue = remoteValue.asString();
  return stringValue.toLowerCase() === 'true';
};

export interface UseFeatureFlagReturn {
  value: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to get feature flag boolean values
 *
 * This is the unified implementation that combines the best features from both
 * shared/utils and sol-buyer/context versions
 *
 * @param key The feature flag key in Firebase Remote Config
 * @param defaultValue Default boolean value to use if the flag can't be retrieved
 * @returns The feature flag boolean value
 */
export function useFeatureFlag(key: string, defaultValue: boolean): boolean {
  const [value, setValue] = useState<boolean>(defaultValue);

  useEffect(() => {
    let isMounted = true;

    const fetchFlag = async () => {
      try {
        await initializeRemoteConfigOnce();
        if (!isMounted) return;

        const remoteConfig = getRemoteConfig();
        const remoteValue = getValue(remoteConfig, key);
        const parsedValue = parseFeatureValue(remoteValue, defaultValue);

        if (process.env.NODE_ENV === 'development') {
          console.log(`🚀 Feature Flag [${key}]:`, {
            value: parsedValue,
            source: remoteValue.getSource(),
            defaultValue,
          });
        }

        const shouldSkipUpdate =
          remoteValue.getSource() === 'static' &&
          Object.is(parsedValue, defaultValue);
        if (!isMounted || shouldSkipUpdate) return;
        setValue(parsedValue);
      } catch (err) {
        console.error('Error fetching feature flag:', err);
        if (!isMounted) return;
        setValue(defaultValue);
      }
    };

    fetchFlag();
    return () => {
      isMounted = false;
    };
  }, [key, defaultValue]);

  return value;
}

/**
 * Enhanced hook that returns loading and error states along with the feature flag value
 *
 * @param key The feature flag key in Firebase Remote Config
 * @param defaultValue Default boolean value to use if the flag can't be retrieved
 * @returns Object containing value, loading state, and error message
 */
export function useFeatureFlagWithState(
  key: string,
  defaultValue = false
): UseFeatureFlagReturn {
  const [state, setState] = useState<UseFeatureFlagReturn>({
    value: defaultValue,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchFlag = async () => {
      try {
        await initializeRemoteConfigOnce();
        if (!isMounted) return;

        const remoteConfig = getRemoteConfig();
        const remoteValue = getValue(remoteConfig, key);
        const parsedValue = parseFeatureValue(remoteValue, defaultValue);

        if (process.env.NODE_ENV === 'development') {
          console.log(`🚀 Feature Flag [${key}]:`, {
            value: parsedValue,
            source: remoteValue.getSource(),
            defaultValue,
          });
        }

        if (isMounted) {
          setState({ value: parsedValue, loading: false, error: null });
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error';
        console.error('Error fetching feature flag:', err);
        if (isMounted) {
          setState({
            value: defaultValue,
            loading: false,
            error: errorMessage,
          });
        }
      }
    };

    fetchFlag();
    return () => {
      isMounted = false;
    };
  }, [key, defaultValue]);

  return state;
}

// Utility functions for testing and debugging
export const getRemoteConfigState = () => ({
  isInitialized: isRemoteConfigInitialized,
  isInitializing: remoteConfigInitPromise !== null,
  error: null,
});

export const resetRemoteConfigState = () => {
  isRemoteConfigInitialized = false;
  remoteConfigInitPromise = null;
};

export const LIST_FEATURE_FLAGS = {
  FF_ADMIN_PO_EFFECTIVE_DATE: 'ff_admin_po_effective_date',
  FF_WEB_PROMO_HIGHLIGHT: 'ff_web_promo_highlight',
  FF_HIGHLIGHT_CG3_NEW: 'ff_highlight_cg3_new',
  FF_MBG_WARNING: 'ff_mbg_warning',
  FF_BANNER_CG3_NEW: 'ff_banner_cg3_new',
  FF_VOUCHERS_MENU: 'ff_vouchers_menu',
  FF_VOUCHERS_DISPLAY: 'ff_vouchers_display',
  FF_NEW_CHECKOUT: 'ff_new_checkout',
  FF_PRODUCT_AUTOCOMPLETE_FALLBACK: 'ff_product_autocomplete_fallback',
  FF_MULTIPLE_DO: 'ff_multiple_do',
  FF_LHC_COLLECTION_MANAGEMENT: 'ff_lhc_collection_management',
  FF_ENABLE_VOUCHER_EXPOSURE: 'ff_enable_voucher_exposure',
};
